// @ts-check

/**
 * @module network/fetch
 */

import NetworkError from './NetworkError';
import { ResponseStatuses } from './responseStatuses';

/**
 * Fires a networked POST request with data serialized to json to an api
 * endpoint with the required options for api calls in itslearning
 * @param {string} url - the url of the endpoint to post the data to
 * @param {object} data - data object to post with the request (will be JSON stringified)
 * @param {object} [options] - Optional options for overriding the fetch defaults
 * @param {string} [options.cache='default'] - cache mode of the request: default, reload, no-cache
 * @param {string} [options.mode=cors] - mode of the request: cors, no-cors, same-origin, navigate
 * @param {string} [options.credentials=same-origin] - credentials of the request: omit, same-origin, include
 * @param {object} [options.headers] - additional headers for the request
 * @returns {Promise} the json response returned by the api or the text if the response is not json
 */
export function post(url, data, options = {}) {
    return new Promise((resolve, reject) => {
        fetch(url, fetchOptions('POST', JSON.stringify(data), options))
            .then(tryParseResponse)
            .then(resolve)
            .catch(reject);
    });
}

/**
 * Fires a networked PUT request with data serialized to json to an api
 * endpoint with the required options for api calls in itslearning
 * @param {string} url - the url of the endpoint to post the data to
 * @param {object} data - data object to post with the request (will be JSON stringified)
 * @param {object} [options] - Optional options for overriding the fetch defaults
 * @param {string} [options.cache='default'] - cache mode of the request: default, reload, no-cache
 * @param {string} [options.mode=cors] - mode of the request: cors, no-cors, same-origin, navigate
 * @param {string} [options.credentials=same-origin] - credentials of the request: omit, same-origin, include
 * @param {object} [options.headers] - additional headers for the request
 * @returns {Promise} the json response returned by the api or the text if the response is not json
 */
export function put(url, data, options = {}) {
    return new Promise((resolve, reject) => {
        fetch(url, fetchOptions('PUT', JSON.stringify(data), options))
            .then(tryParseResponse)
            .then(resolve)
            .catch(reject);
    });
}

/**
 * Fires a networked PATCH request with data serialized to json to an api endpoint
 * @param {string} url - the url of the endpoint to post the data to
 * @param {object} data - data object to post with the request (will be JSON stringified)
 * @param {object} [options] - Optional options for overriding the fetch defaults
 * @param {string} [options.cache='default'] - cache mode of the request: default, reload, no-cache
 * @param {string} [options.mode=cors] - mode of the request: cors, no-cors, same-origin, navigate
 * @param {string} [options.credentials=same-origin] - credentials of the request: omit, same-origin, include
 * @param {object} [options.headers] - additional headers for the request
 * @returns {Promise} the json response returned by the api or the text if the response is not json
 */
export function patch(url, data, options = {}) {
    return new Promise((resolve, reject) => {
        fetch(url, fetchOptions('PATCH', JSON.stringify(data), options))
            .then(tryParseResponse)
            .then(resolve)
            .catch(reject);
    });
}

/**
 * Fires a networked DELETE request with data serialized to json to an api endpoint - delete is a reserved
 * word in javascript so this method is called del
 * @param {string} url - the url of the endpoint to post the data to
 * @param {object} data - data object to post with the request (will be JSON stringified)
 * @param {object} [options] - Optional options for overriding the fetch defaults
 * @param {string} [options.cache='default'] - cache mode of the request: default, reload, no-cache
 * @param {string} [options.mode=cors] - mode of the request: cors, no-cors, same-origin, navigate
 * @param {string} [options.credentials=same-origin] - credentials of the request: omit, same-origin, include
 * @param {object} [options.headers] - additional headers for the request
 * @returns {Promise} the json response returned by the api or the text if the response is not json
 */
export function del(url, data, options = {}) {
    return new Promise((resolve, reject) => {
        fetch(url, fetchOptions('DELETE', JSON.stringify(data), options))
            .then(tryParseResponse)
            .then(resolve)
            .catch(reject);
    });
}

/**
 * Fires a networked GET request to an endpoint
 * @param {string} url - the url of the endpoint to GET
 * @param {object} [options] - Optional options for overriding the fetch defaults
 * @param {string} [options.cache='default'] - cache mode of the request: default, reload, no-cache
 * @param {string} [options.mode=cors] - mode of the request: cors, no-cors, same-origin, navigate
 * @param {string} [options.credentials=same-origin] - credentials of the request: omit, same-origin, include
 * @param {object} [options.headers] - additional headers for the request
 * @returns {Promise} the json response returned by the api or the text if the response is not json
 */
export function get(url, options = {}) {
    return new Promise((resolve, reject) => {
        fetch(url, fetchOptions('GET', undefined, options))
            .then(tryParseResponse)
            .then(resolve)
            .catch(reject);
    });
}

/**
 * @param {Response} response The fetch response which may or may not be json
 * @returns {any} The parsed JSON response, undefined if there is no content or the text content of the response
 * @throws {Error} When response is not OK. And error object would have its response property initialized with rejected resonse object.
 */
export function tryParseResponse(response) {
    if (!response.ok) {
        throw new NetworkError(response.statusText, response);
    }

    // In case of 204 - NO CONTENT return undefined
    if (response.status === ResponseStatuses.noContent) {
        return new Promise((resolve) => resolve(undefined));
    }

    // response body can only be read once so clone before trying .json()
    // so that we can fall back to .text() if needed
    return response.clone().json().catch(() => response.text());
}

/**
 * Fires a networked GET request to an endpoint with the required options for
 * api calls in itslearning
 * @param {string} method - The method this fetch request will use (GET, PATCH, POST, DELETE, PUT)
 * @param {string} [body] - What to send as the fetch request's body
 * @param {object} [options] - Optional options for overriding the fetch defaults
 * @param {string} [options.cache='default'] - cache mode of the request: default, reload, no-cache
 * @param {string} [options.mode=cors] - mode of the request: cors, no-cors, same-origin, navigate
 * @param {string} [options.credentials=same-origin] - credentials of the request: omit, same-origin, include
 * @param {object} [options.headers] - additional headers for the request
 * @returns {object} A RequestInit object for fetch request options
 */
export function fetchOptions(method, body, options) {
    return {
        method,
        mode: options.mode !== undefined ? options.mode : 'cors',
        cache: options.cache !== undefined ? options.cache : 'default',
        credentials: options.credentials !== undefined ? options.credentials : 'same-origin',
        headers: Object.assign({
            'Content-Type': 'application/json; charset=utf-8',
        }, options.headers),
        body,
    };
}
